<!--
  @component

  The radio button component

  ```html
  <oc-radio-button-v1 value="otto" name="brand">Otto</oc-radio-button-v1>
  ```

  @slot required `default` **Mandatory for accessibility!**<br />The label for the radio button element.<br />It provides a brief description of the input field, making it easier for users to understand its purpose.
-->
<svelte:options
  customElement={{
    tag: "oc-radio-button-v1",
    shadow: "none",
    /*                                                         */
    /*         */
    extend: (CustomElementConstructor) =>
      class extends CustomElementConstructor {
        /*                                          */
        static formAssociated = true;

        constructor() {
          super();

          /*                          */
          this.attachShadow({ mode: "open", delegatesFocus: true });

          /*                                */
          this.internals = this.attachInternals();

          this.setValidity = (...args) => this.internals.setValidity(...args);
        }

        /*                                                            */
        get willValidate() {
          return this.internals.willValidate;
        }

        get validity() {
          return this.internals.validity;
        }

        get validationMessage() {
          return this.internals.validationMessage;
        }

        get form() {
          return this.internals.form;
        }

        /*                       */
        formResetCallback() {
          /*                                   */
          this.checked = this.hasAttribute("checked");
        }
      },

    props: {
      name: { type: "String", reflect: true }, /*                                    */
      value: { type: "String" },
      checked: { type: "Boolean" }, /*                                     */
      variant: { type: "String" },
      disabled: { type: "Boolean", reflect: true }, /*                                    */
    },
  }}
/>

<script lang="ts">
  import { usePropertyChange } from "../../../../common/utils/usePropertyChange";
  import { getAssociatedFormElementsByName } from "../../../../common/utils/form";
  import { tag } from "./RadioButtonV1.meta.g";

  usePropertyChange();

  /**
 *
 */
  export let name: string;

  /**
 *
 *
 *
 *
 *
 *
 *
 *
 */
  export let value: string = "on";

  /**
 *
 */
  export let checked = false;

  /**
 *
 *
 *
 */
  export let variant: "default" | "error" = "default";

  /**
 *
 *
 *
 */
  export let disabled: boolean = false;

  /*            */
  export let internals: ElementInternals;

  /*            */
  export let inputTabIndex: 0 | -1 = -1;

  let inputElement: HTMLInputElement;

  $: if (inputElement) {
    internals?.setFormValue(checked ? value : null);
    if (!internals?.form?.noValidate) {
      internals.setValidity(inputElement.validity, inputElement.validationMessage, inputElement);
    }
  }

  /*                                   */
  $: if (name) {
    const radios = getAssociatedFormElementsByName<HTMLOcRadioButtonV1Element>({
      tag,
      internals,
      name,
    });
    if (checked) {
      inputTabIndex = 0;
      radios.forEach((radio) => {
        if (radio.value !== value) {
          radio.checked = false;
          radio.inputTabIndex = -1;
        }
      });
    } else if (radios[0]?.value === value && radios.every((r) => !r.checked)) {
      /*                                                   */
      inputTabIndex = 0;
    } else {
      inputTabIndex = -1;
    }
  }

  /*                                                                                                          */
  const handleArrowKeys = (event: KeyboardEvent) => {
    const radios = getAssociatedFormElementsByName<HTMLOcRadioButtonV1Element>({
      tag,
      internals,
      name,
    });
    const selfIndex = radios.findIndex((radio) => radio.value === value);

    let nextRadio: HTMLOcRadioButtonV1Element | undefined;
    if (["ArrowUp", "ArrowLeft"].includes(event.key)) {
      nextRadio = radios[selfIndex - 1] ?? radios[radios.length - 1];
    } else if (["ArrowDown", "ArrowRight"].includes(event.key)) {
      nextRadio = radios[selfIndex + 1] ?? radios[0];
    }
    if (nextRadio) {
      nextRadio.checked = true;
      nextRadio.focus();
      event.stopPropagation();
      event.preventDefault();
    }
  };
</script>

<label class="radio" class:radio--error={variant === "error"} class:radio--disabled={disabled}>
  <input
    class="radio__input"
    id="input"
    bind:this={inputElement}
    type="radio"
    {value}
    {disabled}
    {name}
    {checked}
    tabindex={inputTabIndex}
    on:change={() => {
      checked = !checked;
    }}
    on:keydown={handleArrowKeys}
  />
  <slot />
</label>

<style lang="scss">
  @use "RadioButtonV1.config" as config;
  @use "@otto-ec/design-tokens/tokens";
  @use "../../../../common/scss/focus.scss";

  :host {
    @include config.oc-radio-button;
    padding-left: unset !important; /*                           */
  }

  .radio {
    box-sizing: border-box;
    position: relative;
    display: block;
    padding-left: config.$label-padding-left;

    color: tokens.$oc-component-form-default-label-color;

    &__input {
      @include focus.styles();

      all: unset;

      position: absolute;
      top: 0;
      left: 0;

      width: tokens.$oc-component-form-radio-button-container-size;
      height: tokens.$oc-component-form-radio-button-container-size;

      outline: 1px solid tokens.$oc-component-form-default-border-color;
      border-radius: 50%;

      &:hover:not(:disabled) {
        outline-width: 2px;
      }

      &:active {
        outline-color: tokens.$oc-component-form-default-border-color-active;
      }

      &:focus {
        outline-color: tokens.$oc-component-form-default-border-color-focus;
      }

      /*                         */
      &:checked:before {
        $center-dot: 7px;

        content: "";
        display: block;
        position: absolute;
        left: $center-dot;
        top: $center-dot;
        bottom: $center-dot;
        right: $center-dot;

        border-radius: 50%;
        background: tokens.$oc-component-form-default-icon-color;
      }
    }

    &--error {
      color: tokens.$oc-component-form-error-label-color;

      .radio__input {
        outline-color: tokens.$oc-component-form-error-border-color;

        &:checked:before {
          background: tokens.$oc-component-form-error-icon-color;
        }
      }
    }

    &--disabled {
      color: tokens.$oc-component-form-disabled-label-color;

      .radio__input {
        outline-color: tokens.$oc-component-form-disabled-border-color;
        background-color: tokens.$oc-component-form-disabled-background-color;

        &:checked:before {
          background: tokens.$oc-component-form-disabled-icon-color;
        }
      }
    }
  }
</style>
